import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["type"],
  _excluded2 = ["type"];
import { convertJsonToFieldValueAvro } from 'rpc-client-utils';
import { DELTA_ID_PREFIX } from '../reducer/types';
import { CONTENT_BLOCK_TYPENAME_PREFIX, ABLY_TYPE_TO_CHIRP_SUFFIX, TOOL_USE_BLOCK_TYPES } from './ablyConstants';
export function createDeltaAccumulator() {
  let deltaIdCounter = 0;
  let messageShell = null;
  let blocks = [];
  let currentDeltaId = null;
  const activeBlockIndices = new Set();
  const inputJsonBuffers = new Map();
  function isRenderSafe(index, block) {
    if (!TOOL_USE_BLOCK_TYPES.has(block.type)) return true;
    return !activeBlockIndices.has(index);
  }
  function buildSnapshot() {
    if (!messageShell) return null;
    const safeBlocks = blocks.filter((block, index) => block && isRenderSafe(index, block));
    if (safeBlocks.length === 0) return null;
    return Object.assign({}, messageShell, {
      id: currentDeltaId,
      content: safeBlocks.map(_ref => {
        let rest = _objectWithoutPropertiesLoose(_ref, _excluded);
        return rest;
      })
    });
  }
  return {
    handleEvent(event) {
      const eventType = event.type;
      if (!eventType) return null;
      switch (eventType) {
        case 'MESSAGE_START':
          {
            deltaIdCounter += 1;
            currentDeltaId = `${DELTA_ID_PREFIX}${deltaIdCounter}`;
            const now = Date.now();
            messageShell = {
              role: 'ASSISTANT',
              timestamp: now,
              inputTokensUsed: 0,
              outputTokensUsed: 0,
              cacheCreationInputTokens: 0,
              cacheReadInputTokens: 0,
              timing: {
                initiatedAt: now
              },
              extraMetadata: {}
            };
            blocks = [];
            activeBlockIndices.clear();
            inputJsonBuffers.clear();
            return null;
          }
        case 'CONTENT_BLOCK_START':
          {
            var _ABLY_TYPE_TO_CHIRP_S;
            // Guard against missing MESSAGE_START (can be dropped if the channel
            // attachment completes after the backend publishes it).
            if (!messageShell) {
              deltaIdCounter += 1;
              currentDeltaId = `${DELTA_ID_PREFIX}${deltaIdCounter}`;
              const now = Date.now();
              messageShell = {
                role: 'ASSISTANT',
                timestamp: now,
                inputTokensUsed: 0,
                outputTokensUsed: 0,
                cacheCreationInputTokens: 0,
                cacheReadInputTokens: 0,
                timing: {
                  initiatedAt: now
                },
                extraMetadata: {}
              };
              blocks = [];
              activeBlockIndices.clear();
              inputJsonBuffers.clear();
            }
            const index = event.index;
            const contentBlock = event.contentBlock;
            const blockType = contentBlock.type;
            const suffix = (_ABLY_TYPE_TO_CHIRP_S = ABLY_TYPE_TO_CHIRP_SUFFIX[blockType]) !== null && _ABLY_TYPE_TO_CHIRP_S !== void 0 ? _ABLY_TYPE_TO_CHIRP_S : `${blockType}ChirpContentBlock`;
            const rest = _objectWithoutPropertiesLoose(contentBlock, _excluded2);
            blocks[index] = Object.assign({}, rest, {
              type: blockType,
              __typename: `${CONTENT_BLOCK_TYPENAME_PREFIX}${suffix}`
            });
            activeBlockIndices.add(index);
            return null;
          }
        case 'CONTENT_BLOCK_DELTA':
          {
            const index = event.index;
            const contentBlockDelta = event.contentBlockDelta;
            if (!contentBlockDelta) return buildSnapshot();
            const deltaType = contentBlockDelta.type;
            const block = blocks[index];
            if (!block) return null;
            if (deltaType === 'TEXT_DELTA') {
              var _ref2;
              block.text = ((_ref2 = block.text) !== null && _ref2 !== void 0 ? _ref2 : '') + contentBlockDelta.delta;
            } else if (deltaType === 'THINKING_DELTA') {
              var _ref3;
              block.thinking = ((_ref3 = block.thinking) !== null && _ref3 !== void 0 ? _ref3 : '') + contentBlockDelta.delta;
            } else if (deltaType === 'TOOL_INPUT_DELTA') {
              var _inputJsonBuffers$get;
              const existing = (_inputJsonBuffers$get = inputJsonBuffers.get(index)) !== null && _inputJsonBuffers$get !== void 0 ? _inputJsonBuffers$get : '';
              inputJsonBuffers.set(index, existing + contentBlockDelta.partialJsonDelta);
            } else if (deltaType === 'DISPLAY_ONLY_DELTA') {
              const {
                delta,
                deltaField
              } = contentBlockDelta;
              const value = block.value;
              if (value !== null && value !== void 0 && value[0] && typeof deltaField === 'string') {
                var _ref4;
                const prev = (_ref4 = value[0][deltaField]) !== null && _ref4 !== void 0 ? _ref4 : '';
                block.value = [Object.assign({}, value[0], {
                  [deltaField]: prev + (delta !== null && delta !== void 0 ? delta : '')
                }), ...value.slice(1)];
              }
            }
            return buildSnapshot();
          }
        case 'CONTENT_BLOCK_STOP':
          {
            const index = event.index;
            activeBlockIndices.delete(index);
            const block = blocks[index];
            if (block && TOOL_USE_BLOCK_TYPES.has(block.type)) {
              const jsonBuffer = inputJsonBuffers.get(index);
              if (jsonBuffer) {
                try {
                  const parsed = JSON.parse(jsonBuffer);
                  block.input = convertJsonToFieldValueAvro(parsed);
                } catch (_unused) {
                  // leave input as-is if JSON is malformed
                }
                inputJsonBuffers.delete(index);
              }
            }
            return buildSnapshot();
          }
        default:
          return null;
      }
    },
    reset() {
      messageShell = null;
      blocks = [];
      currentDeltaId = null;
      activeBlockIndices.clear();
      inputJsonBuffers.clear();
    }
  };
}